import type { TerminalImportStatus } from '../../common/document-import/uploadStatus';
import type { DocumentOrigin, ImportCompletionSource } from './tracking.upload';
import { trackFileImportCompleted, type UploadTrackingContext } from './uploadAnalytics';

const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

/** Origin passed to registerChildImports; the parent is always 'single'. */
export type ChildDocumentOrigin = Exclude<DocumentOrigin, 'single'>;

interface PendingEntry {
  ctx: UploadTrackingContext;
  documentOrigin: DocumentOrigin;
  parentDocumentId?: string;
  registeredAt: number;
}

interface EarlyTerminalEntry {
  terminalStatus: TerminalImportStatus;
  completionSource: ImportCompletionSource;
  bufferedAt: number;
}

const pendingImports = new Map<string, PendingEntry>();
const completedDocumentIds = new Set<string>();
const earlyTerminals = new Map<string, EarlyTerminalEntry>();

function emitImportCompleted(
  documentId: string,
  entry: PendingEntry,
  terminalStatus: TerminalImportStatus,
  completionSource: ImportCompletionSource,
): void {
  completedDocumentIds.add(documentId);
  pendingImports.delete(documentId);

  trackFileImportCompleted({
    documentId,
    uploadJobId: entry.ctx.uploadJobId,
    uploadSource: entry.ctx.uploadSource,
    flowType: entry.ctx.flowType,
    batchUploadId: entry.ctx.batchUploadId,
    startDate: entry.ctx.startDate,
    terminalStatus,
    completionSource,
    documentOrigin: entry.documentOrigin,
    parentDocumentId: entry.parentDocumentId,
  });
}

function drainEarlyTerminal(documentId: string, entry: PendingEntry): void {
  const early = earlyTerminals.get(documentId);
  if (!early) return;

  earlyTerminals.delete(documentId);
  emitImportCompleted(documentId, entry, early.terminalStatus, early.completionSource);
}

export function pruneExpiredPendingImports(): void {
  const now = Date.now();
  for (const [documentId, entry] of pendingImports) {
    if (now - entry.registeredAt > PENDING_TTL_MS) {
      pendingImports.delete(documentId);
    }
  }
  for (const [documentId, entry] of earlyTerminals) {
    if (now - entry.bufferedAt > PENDING_TTL_MS) {
      earlyTerminals.delete(documentId);
    }
  }
}

export function registerPendingImport(documentId: string, ctx: UploadTrackingContext): void {
  pruneExpiredPendingImports();

  const entry: PendingEntry = { ctx, documentOrigin: 'single', registeredAt: Date.now() };
  pendingImports.set(documentId, entry);
  drainEarlyTerminal(documentId, entry);
}

export function registerChildImports(
  parentDocumentId: string,
  childIds: string[],
  origin: ChildDocumentOrigin,
): void {
  pruneExpiredPendingImports();

  const parent = pendingImports.get(parentDocumentId);
  if (!parent) return;

  const registeredAt = Date.now();
  for (const childId of childIds) {
    const entry: PendingEntry = {
      ctx: parent.ctx,
      documentOrigin: origin,
      parentDocumentId,
      registeredAt,
    };
    pendingImports.set(childId, entry);
    drainEarlyTerminal(childId, entry);
  }
}

export function notifyImportAnalysisComplete(
  documentId: string,
  terminalStatus: TerminalImportStatus,
  completionSource: ImportCompletionSource,
): void {
  pruneExpiredPendingImports();

  if (completedDocumentIds.has(documentId)) return;

  const entry = pendingImports.get(documentId);
  if (entry) {
    emitImportCompleted(documentId, entry, terminalStatus, completionSource);
    return;
  }

  earlyTerminals.set(documentId, {
    terminalStatus,
    completionSource,
    bufferedAt: Date.now(),
  });
}

/** Clears all pending file-import state. Call when the user dismisses the upload panel. */
export function clearFileImportCompletedState(): void {
  pendingImports.clear();
  completedDocumentIds.clear();
  earlyTerminals.clear();
}

/** @internal Test helper */
export function resetFileImportCompletedTracker(): void {
  clearFileImportCompletedState();
}
